/**
 * Metric Normalization field — ported from xfactor-data-pipeline settings editor.
 * Uses our Button + CommonModal instead of shadcn Dialog.
 */
import React, { useEffect, useMemo, useState } from "react";

import { Settings2 } from "lucide-react";

import {
  CUSTOM_METHODS,
  DIRECTION_LABELS,
  TECHNIQUES,
  type NormDirection,
  type NormType,
  type NormalizationValue,
  normalizeLegacyType,
  summarizeNormalization,
  techniqueSpec,
  validateNormalization,
} from "./metricNormalizationConfig";
import Button from "../../Components/Button";
import CommonModal from "../../Components/Modal";

export type { NormalizationValue };

export function NormalizationField({
  value,
  onChange,
  required = true,
}: {
  value: Partial<NormalizationValue> | null;
  onChange: (v: NormalizationValue) => void;
  required?: boolean;
}) {
  const [open, setOpen] = useState(false);

  const normalizedValue = useMemo<Partial<NormalizationValue> | null>(() => {
    if (!value?.normalization_type) return value ?? null;
    const legacy = normalizeLegacyType(value.normalization_type);
    if (
      legacy.type &&
      legacy.type !== value.normalization_type &&
      legacy.direction
    ) {
      return {
        ...value,
        normalization_type: legacy.type,
        normalization_direction:
          value.normalization_direction ?? legacy.direction,
      };
    }
    return value;
  }, [value]);

  const isSet =
    !!normalizedValue?.normalization_type &&
    !!normalizedValue?.normalization_direction;
  const summary = summarizeNormalization(normalizedValue ?? {});

  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-medium text-gray-600">
        Metric Normalization
        {required ? <span className="ml-0.5 text-red-600">*</span> : null}
      </label>
      <div className="flex items-center gap-2">
        <div
          className={`flex-1 rounded-md border px-3 py-2 text-xs ${
            isSet
              ? "border-gray-200 bg-gray-50 text-gray-800"
              : "border-dashed border-red-300 bg-red-50 text-red-700"
          }`}
        >
          {isSet ? summary : "Not set — click to configure"}
        </div>
        <Button
          type="button"
          variant="outline-primary"
          onClick={() => setOpen(true)}
          icon={<Settings2 className="h-3.5 w-3.5" />}
        >
          {isSet ? "Change" : "Configure"}
        </Button>
      </div>
      <NormalizationPickerDialog
        open={open}
        value={normalizedValue}
        onClose={() => setOpen(false)}
        onConfirm={v => {
          onChange(v);
          setOpen(false);
        }}
      />
    </div>
  );
}

function NormalizationPickerDialog({
  open,
  value,
  onClose,
  onConfirm,
}: {
  open: boolean;
  value: Partial<NormalizationValue> | null;
  onClose: () => void;
  onConfirm: (v: NormalizationValue) => void;
}) {
  const [draft, setDraft] = useState<Partial<NormalizationValue>>(value ?? {});

  useEffect(() => {
    if (open) setDraft(value ?? {});
  }, [open, value]);

  const spec = draft.normalization_type
    ? techniqueSpec(draft.normalization_type)
    : null;

  useEffect(() => {
    if (!spec) return;
    if (spec.id === "custom_scaling" && draft.custom_method) {
      const m = CUSTOM_METHODS.find(c => c.id === draft.custom_method);
      if (m && draft.normalization_direction !== m.direction) {
        setDraft(d => ({ ...d, normalization_direction: m.direction }));
      }
      return;
    }
    if (spec.allowedDirections.length === 1) {
      const only = spec.allowedDirections[0];
      if (draft.normalization_direction !== only) {
        setDraft(d => ({ ...d, normalization_direction: only }));
      }
    }
  }, [spec, draft.custom_method, draft.normalization_direction]);

  const { ok, errors } = useMemo(() => validateNormalization(draft), [draft]);

  const setType = (t: NormType) => {
    setDraft(d => {
      const next: Partial<NormalizationValue> = {
        ...d,
        normalization_type: t,
      };
      const s = techniqueSpec(t);
      if (
        !next.normalization_direction ||
        !s.allowedDirections.includes(next.normalization_direction)
      ) {
        next.normalization_direction =
          s.allowedDirections.length === 1 ? s.allowedDirections[0] : undefined;
      }
      if (!s.requires.scaleRange) {
        next.scale_min = null;
        next.scale_max = null;
      }
      if (!s.requires.customMethod) {
        next.custom_method = null;
      }
      return next;
    });
  };

  return (
    <CommonModal
      isOpen={open}
      toggle={onClose}
      title="Metric Normalization"
      subtitle="Pick a technique and direction. Selection is applied back to the metric form."
      size="2xl"
      usePortal
      nested
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline-primary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={!ok}
            onClick={() => {
              if (!ok) return;
              onConfirm(draft as NormalizationValue);
            }}
          >
            Apply
          </Button>
        </div>
      }
    >
      <div className="max-h-[60vh] space-y-5 overflow-y-auto py-1">
        <section className="space-y-2">
          <p className="m-0 text-xs font-semibold uppercase tracking-wide text-gray-500">
            1 · Technique
          </p>
          <div className="grid gap-2">
            {TECHNIQUES.map(t => {
              const selected = draft.normalization_type === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setType(t.id)}
                  className={`rounded-md border p-3 text-left transition-colors ${
                    selected
                      ? "border-violet-500 bg-violet-50"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-gray-900">
                      {t.label}
                    </span>
                    <span className="font-mono text-[10px] text-gray-500">
                      {t.id}
                    </span>
                  </div>
                  <p className="mb-0 mt-1 text-xs text-gray-500">
                    {t.description}
                  </p>
                </button>
              );
            })}
          </div>
        </section>

        {spec ? (
          <section className="space-y-2">
            <p className="m-0 text-xs font-semibold uppercase tracking-wide text-gray-500">
              2 · Direction
            </p>
            <div className="flex flex-wrap gap-2">
              {(
                ["higher_better", "lower_better", "neutral"] as NormDirection[]
              ).map(d => {
                const allowed = spec.allowedDirections.includes(d);
                const selected = draft.normalization_direction === d;
                const locked =
                  spec.directionLocked ||
                  spec.allowedDirections.length === 1 ||
                  (spec.id === "custom_scaling" && !!draft.custom_method);
                return (
                  <button
                    key={d}
                    type="button"
                    disabled={!allowed || (locked && !selected)}
                    onClick={() =>
                      setDraft(prev => ({
                        ...prev,
                        normalization_direction: d,
                      }))
                    }
                    className={`rounded-md border px-3 py-1.5 text-xs transition-colors ${
                      selected
                        ? "border-violet-500 bg-violet-50 text-gray-900"
                        : "border-gray-200 text-gray-600 hover:bg-gray-50"
                    } ${
                      !allowed || (locked && !selected)
                        ? "cursor-not-allowed opacity-40"
                        : ""
                    }`}
                  >
                    {DIRECTION_LABELS[d]}
                  </button>
                );
              })}
            </div>
            {(spec.directionLocked || spec.allowedDirections.length === 1) && (
              <p className="m-0 text-[11px] text-gray-500">
                Direction is fixed by this technique.
              </p>
            )}
          </section>
        ) : null}

        {spec?.requires.scaleRange ? (
          <section className="space-y-2">
            <p className="m-0 text-xs font-semibold uppercase tracking-wide text-gray-500">
              3 · Scale range
            </p>
            <div className="grid grid-cols-2 gap-3">
              <label className="block space-y-1 text-xs text-gray-600">
                Scale min
                <input
                  type="number"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={draft.scale_min ?? ""}
                  onChange={e =>
                    setDraft(d => ({
                      ...d,
                      scale_min:
                        e.target.value === "" ? null : Number(e.target.value),
                    }))
                  }
                />
              </label>
              <label className="block space-y-1 text-xs text-gray-600">
                Scale max
                <input
                  type="number"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={draft.scale_max ?? ""}
                  onChange={e =>
                    setDraft(d => ({
                      ...d,
                      scale_max:
                        e.target.value === "" ? null : Number(e.target.value),
                    }))
                  }
                />
              </label>
            </div>
          </section>
        ) : null}

        {spec?.requires.customMethod ? (
          <section className="space-y-2">
            <p className="m-0 text-xs font-semibold uppercase tracking-wide text-gray-500">
              3 · Custom method
            </p>
            <div className="grid gap-2">
              {CUSTOM_METHODS.map(m => {
                const selected = draft.custom_method === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() =>
                      setDraft(d => ({
                        ...d,
                        custom_method: m.id,
                        normalization_direction: m.direction,
                      }))
                    }
                    className={`rounded-md border p-3 text-left transition-colors ${
                      selected
                        ? "border-violet-500 bg-violet-50"
                        : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium">{m.label}</span>
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600">
                        {DIRECTION_LABELS[m.direction]}
                      </span>
                    </div>
                    <p className="mb-0 mt-1 text-xs text-gray-500">
                      {m.description}
                    </p>
                  </button>
                );
              })}
            </div>
          </section>
        ) : null}

        {errors.length > 0 && draft.normalization_type ? (
          <ul className="m-0 space-y-0.5 text-xs text-red-600">
            {errors.map((e, i) => (
              <li key={i}>• {e}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </CommonModal>
  );
}
